import { Loader2, Search, X } from "lucide-react"
import { useEffect, useRef } from "react"
import { Kbd } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"

/**
 * Полей поиска на экране бывает два: страница Библиотеки под открытой поверх неё
 * модалкой выбора кадра. Слэш обязан попасть в верхнее, поэтому смонтированные поля
 * складываются стопкой и горячую клавишу обрабатывает последнее — то, что открылось
 * позже. Порядок в DOM для этого не годится: портал модалки лежит вне поддерева страницы.
 */
const mountedFields: HTMLInputElement[] = []

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
}

export type ToolbarSearchFieldProps = {
  value: string
  onChange: (next: string) => void
  placeholder: string
  title: string
  clearLabel: string
  loading?: boolean
  autoFocus?: boolean
  /** Слэш фокусирует поле. В ящике заметок клавиша не нужна: поле монтируется позже тулбара и перехватило бы её. */
  slashHotkey?: boolean
  maxLength?: number
  className?: string
}

export function ToolbarSearchField({
  value,
  onChange,
  placeholder,
  title,
  clearLabel,
  loading = false,
  autoFocus = false,
  slashHotkey = true,
  maxLength,
  className,
}: ToolbarSearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const input = inputRef.current
    if (!input || !slashHotkey) return
    mountedFields.push(input)

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return
      if (isTypingTarget(e.target)) return
      if (mountedFields[mountedFields.length - 1] !== input) return
      // preventDefault обязателен: иначе слэш дописывается в поле сразу после фокуса.
      e.preventDefault()
      input.focus()
      input.select()
    }

    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      const index = mountedFields.indexOf(input)
      if (index >= 0) mountedFields.splice(index, 1)
    }
  }, [slashHotkey])

  // Модалка выбора кадра фокус себе не забирает намеренно (иначе Enter уходил бы в
  // крестик вместо подтверждения выбора), и он оставался там, где был, — сплошь и рядом
  // в редакторе промпта под модалкой. Тогда и слэш попадал туда же: горячая клавиша
  // молчит, когда курсор стоит в тексте. Поэтому фокус берёт поле поиска — заодно можно
  // сразу печатать запрос, не целясь мышью.
  useEffect(() => {
    if (!autoFocus) return
    // Кадр отступа: Radix возвращает фокус в конце собственного открытия, и фокус,
    // поставленный синхронно, он бы перебил.
    const frame = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [autoFocus])

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-2 rounded-lg border border-ui-border px-2.5",
        "text-ui-text-muted focus-within:border-ui-border-strong",
        className,
      )}
      title={title}
    >
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
      )}
      {/* border-0 обязателен: рамку рисует внешний span, а собственная рамка input
          от браузера даёт коробку в коробке. */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Escape") return
          // Escape в поле гасим сами: в модалке он дошёл бы до Radix и закрыл пикер
          // вместе с набранным выбором.
          e.stopPropagation()
          if (value) onChange("")
          else e.currentTarget.blur()
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        // p-0 и border-0 обязательны: @tailwindcss/forms вешает на каждый текстовый input
        // padding 0.75rem и рамку, и текст отъезжает от иконки на этот padding плюс gap.
        // focus:ring-0 — не браузерная обводка: тот же плагин на :focus ставит синее кольцо
        // blue-600 через box-shadow, и outline-утилитами оно не снимается. Фокус здесь
        // показывает рамка внешнего span, поэтому кольцо на самом input гасим до нуля.
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-ui-text-primary placeholder:text-ui-text-faint focus:ring-0 focus:outline-none"
      />
      {/* Слот справа фиксированной ширины: крестик и подсказка сменяют друг друга при
          наборе, и без общей коробки поле дёргалось бы на каждый первый символ.
          Без горячей клавиши пустой слот не резервируем — подсказки «/» там нет. */}
      {slashHotkey || value ? (
        <span className="flex w-6 shrink-0 items-center justify-center">
          {value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              aria-label={clearLabel}
              title={clearLabel}
              className="rounded text-ui-text-muted transition-colors hover:text-ui-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : (
            // Подсказка мелкая и приземистая: у Kbd по умолчанию размер шрифта наследуется от
            // родителя, отчего слэш выходил вдвое крупнее макетного и коробка тянулась вверх.
            <Kbd className="hidden h-4 items-center justify-center border-ui-border bg-transparent px-1.5 text-[10px] leading-none text-ui-text-faint sm:inline-flex">
              /
            </Kbd>
          )}
        </span>
      ) : null}
    </span>
  )
}
